<script lang="ts" setup>
import { computed, onMounted, onUnmounted, provide, ref, watch } from 'vue'
import { useInterval, useScroll, watchThrottled } from '@vueuse/core'
import { useRoute, useRouter } from 'vue-router/composables'
import { useChapterTheme } from './composables/useChapterTheme'
import { useChapterLoader } from './composables/useChapterLoader'
import { useChapterTracking } from './composables/useChapterTracking'
import { useChapterScroll } from './composables/useChapterScroll'
import ChapterThemeSettings from './components/ChapterThemeSettings.vue'
import ChapterUnlock from './components/ChapterUnlock.vue'
import ChapterNavigation from './components/ChapterNavigation.vue'
import ChapterLeadSentence from './components/ChapterLeadSentence.vue'
import ChapterCoinsSuccess from './components/ChapterCoinsSuccess.vue'
import ChapterRecommend from './components/ChapterRecommend.vue'
import ChapterPayTemplate from './components/ChapterPayTemplate.vue'
import { chapterConfig as rocnovelConfig } from './configs/rocnovel'
import { chapterConfig as ocereadConfig } from './configs/oceread'
import { chapterConfig as ficmoonConfig } from './configs/ficmoon'
import WelfareGuideOpen from '@/pages/welfare/components/WelfareGuideOpen.vue'
import { loadBrandConfig } from '@/utils/loadBrandConfig'
import { $formatErrorMessage } from '@/helpers'
import { api } from '@/api'
import { CHAPTER_LOCK, ERROR_PAGE_LEVEL, VIP_TYPE } from '@/constants'
import { useToastStore, useUserStore } from '@/stores'
import { useGotoBack } from '@/composables'
import { bannerShow, useBannerShow, useCallRocnovelApp } from '@/composables/useBannerShow'
import { isInThirdAppBrowser } from '@/utils/bowserEnv'
import { directPageTimeReport, pageViewTrack, useBookRead, useChapterReadComplete, useElementClick } from '@/composables/tracks'
import { useWheelOrSwipeUpListener } from '@/composables/useWheelOrSwipeUpListener'
import { brand } from '@/config/brands'

const props = defineProps<{
  contentId: string
  chapterId: string
}>()

// 加载当前品牌配置 (构建时tree-shaking)
const config = loadBrandConfig({
  rocnovel: rocnovelConfig,
  oceread: ocereadConfig,
  ficmoon: ficmoonConfig,
})

const route = useRoute()
const router = useRouter()
const { $toast } = useToastStore()
const gotoBack = useGotoBack()
const userStore = useUserStore()
const { readCompleteTrack, resetReadComplete, chapterVisitTrack, readCompleteTrackDirect } = useChapterReadComplete()
const { elementClick } = useElementClick()
const { start: startReadTime, bookRead } = useBookRead()
const { isTrigger: isTriggerWheelOrSwipeUp, listenWheelOrSwipeUp, removeWheelOrSwipeUpListener } = useWheelOrSwipeUpListener()

// 基础状态
const skeleton = ref(true)
const popupRecharge = ref(false)
const showRecommend = ref(false)
const coinsSuccess = ref(false)
const book: any = ref<GetContentGetcontentdetailResponse['data']>()
const discountPayInfo = ref<Record<string, any>>({
  discountOnceCoins: 0,
  bookImageUrl: '',
})
const showSaveUrl = ref(false)
const showGuidePopup = ref(false)
const containerRef = ref<HTMLElement>()
const chapterRef = ref<HTMLElement>()
const chapter: any = ref<PostReadPlayResponse['data'] | null>()
const content = ref('')
const chapterList = ref<any>([])
const landingList = ref<string[]>([])
const popupTOC = ref(false)
const popupTheme = ref(false)
const enterTime = ref(Date.now())
const showCompleteRecommendLoading = ref(false)
const chapterUnlockRef = ref<any>()

provide('containerRef', containerRef)
provide('chapterConfig', config)

// 使用主题管理 composable (传入品牌默认主题配置)
const { theme, THEME_FONT } = useChapterTheme(config.themeDefaults)
provide('theme', theme)

// 计算属性
const paragraphs = computed(() => {
  if (!content.value)
    return []
  return content.value.split(/[\n]/).filter(Boolean)
})

const currentChapter = computed(() => {
  return chapterList.value.find((item: any) => item.id === props.chapterId)
})

const isLastChapter = computed(() => {
  return chapterList.value.length > 0 && chapterList.value[chapterList.value.length - 1].id === props.chapterId
})

const showDownloadApp = computed(() => {
  return !!(bannerShow.value && chapter.value && chapter.value.sequenceNo)
})

// 滚动相关
const { y, arrivedState } = useScroll(containerRef)
const { reset: resetReadTime } = useInterval(1000, { controls: true })

// 使用滚动管理 composable
const { reportScrollLocation, handleReadComplete } = useChapterScroll({
  contentId: computed(() => props.contentId),
  chapterId: computed(() => props.chapterId),
  chapter,
  chapterList,
  containerRef,
  chapterRef,
  y,
  readCompleteTrack,
})

// 使用埋点上报 composable
const { handleReadTrack, attributionTrack, handleCloseEvent } = useChapterTracking({
  contentId: computed(() => props.contentId),
  chapterId: computed(() => props.chapterId),
  book,
  chapter,
  bookRead,
})

// 错误处理
function onErrorHandle() {
  router.replace({
    name: 'error',
    query: {
      level: ERROR_PAGE_LEVEL.DEFAULT,
      message: 'Network loading failure',
      redirect: window.encodeURIComponent(window.location.href),
    },
  })
}

// 使用章节加载 composable
const { loadChapter, fetchChapter, fetchLatestPosition } = useChapterLoader({
  contentId: computed(() => props.contentId),
  chapterId: computed(() => props.chapterId),
  book,
  chapter,
  content,
  chapterList,
  containerRef,
  skeleton,
  onChapterVisitTrack: chapterVisitTrack,
  onReportScrollLocation: reportScrollLocation,
  onErrorHandle,
})

/**
 * 获取书籍信息
 */
async function fetchBook() {
  const { data } = await api.get<any, GetContentGetcontentdetailResponse>('/content/getContentDetail', { params: { contentId: props.contentId } })
  book.value = data
  // 支付成功且类型是折扣充值则显示成功弹窗
  if (route.query.payment_status === 'success' && Number(route.query?.subscribeType) === VIP_TYPE.DISCOUNT && userStore.$state.landingPageId && userStore.$state.landingPageContentId === props.contentId) {
    coinsSuccess.value = true
    discountPayInfo.value.discountOnceCoins = route.query.discountOnceCoins
    discountPayInfo.value.bookImageUrl = data.contentCoverUrl

    pageViewTrack('sale')
  }
}

/**
 * 获取章节目录
 */
async function fetchChapterList() {
  try {
    const { data: chapterListData } = await api.get<any>('/content/getContentChapterBookList', {
      params: {
        contentId: props.contentId,
        pageIndex: 1,
        pageSize: 1500,
      },
    })
    chapterList.value = chapterListData.records
  }
  catch (error) {
    onErrorHandle()
  }
}

/**
 * 获取引导语句
 */
async function getLeadSentence() {
  const { data } = await api.get<any, GetContentGetLeadSentenceResponse>(`/content/getLeadSentence?contentId=${props.contentId}`)
  if (!data)
    return
  landingList.value = data?.split('\n')?.filter(Boolean) || []
}

/**
 * 上一章
 */
function onPrevChapter() {
  if (!chapter.value!.prev)
    return

  elementClick('prev_chapter')
  router.replace({ name: 'chapter', params: { contentId: props.contentId, chapterId: chapter.value!.prev.contentChapterId } })
}

/**
 * 下一章
 */
function onNextChapter() {
  // 用户激活上报
  attributionTrack()
  // 完读上报
  if (currentChapter.value.lockStatus !== CHAPTER_LOCK.LOCK)
    readCompleteTrackDirect(props.contentId, props.chapterId, currentChapter.value.sequenceNo, currentChapter.value.chapterVersionId)
  elementClick('next_chapter')

  if (!chapter.value!.next)
    finishRecommend()
  else
    router.replace({ name: 'chapter', params: { contentId: props.contentId, chapterId: chapter.value!.next.contentChapterId } })
}

/**
 * 打开目录
 */
function onOpenTOC() {
  popupTOC.value = true
  attributionTrack()
}

/**
 * 目录检查
 */
function onTOCCheckPass() {
  return true
}

/**
 * 切换主题设置
 */
function onToggleTheme() {
  popupTOC.value = false
  popupTheme.value = !popupTheme.value
}

/**
 * 解锁章节
 */
async function onUnlockChapter() {
  if (!chapter.value)
    return

  if (chapter.value.priceAmount > userStore.$state.coinBalance) {
    popupRecharge.value = true
    const newQuery = { ...route.query, isShowpopupRecharge: '1' }
    router.replace({ path: route.path, query: newQuery })
  }

  const autoLock = chapterUnlockRef.value?.autoLock
  if (autoLock !== undefined && autoLock !== userStore.$state.autoLock) {
    await api.post('/member/update/auto-lock', {
      autoLock,
    })
    await userStore.init({ force: true })
  }
  fetchChapter(resetReadTime)
}

/**
 * 完读推荐
 */
function finishRecommend() {
  showCompleteRecommendLoading.value = true
  setTimeout(() => {
    showCompleteRecommendLoading.value = false
    router.push({
      name: 'recommend',
      params: { contentId: props.contentId },
      query: { status: book.value?.finishStatus === 0 ? 'ongoing' : 'finish' },
    })
  }, 800)
}

/**
 * 添加推荐参数
 */
function appendRecommendParams() {
  const url = new URL(window.location.href)
  url.searchParams.set('recommend', '1')
  history.replaceState({}, '', url.href)
}

/**
 * 处理充值成功
 */
function handleCoinsSuccess() {
  coinsSuccess.value = false
  const url = new URL(window.location.href)
  url.searchParams.delete('subscribeType')
  url.searchParams.delete('discountOnceCoins')
  history.replaceState({}, '', url.href)
}

// 拉起 app
const { callApp } = useCallRocnovelApp()

function handleCallApp(contentId: string, sequenceNo: number) {
  callApp(contentId, sequenceNo)
}

// Mounted
onMounted(async () => {
  // 如果检测到页面是从"往返缓存"中读取的,刷新页面
  window.addEventListener('pageshow', (e) => {
    if (e.persisted)
      window.location.reload()
  })

  // 页面直接关闭时,需上报阅读事件
  window.addEventListener('beforeunload', handleCloseEvent)

  // 只有从支付返回时,显示推荐
  if (route.query.recommend && !route.query.payment_status)
    showRecommend.value = true

  if (route.query.isShowpopupRecharge === '1' && !route.query.payment_status)
    popupRecharge.value = true

  // 展示 Save Url 按钮
  if (route.query.payment_status === 'success' && isInThirdAppBrowser())
    showSaveUrl.value = true

  skeleton.value = true
  try {
    getLeadSentence()
    await fetchBook()
    await fetchChapterList()

    if (!route.query.skipPosition) {
      const existed = await fetchLatestPosition()
      if (existed)
        return
    }
    await loadChapter()
    startReadTime.value = new Date().getTime()
  }
  catch (error) {
    $toast($formatErrorMessage(error))
  }
  finally {
    skeleton.value = false
  }

  useBannerShow()
  listenWheelOrSwipeUp()
})

// Watches
watch(() => props.contentId, () => {
  getLeadSentence()
  fetchBook()
})

watch(() => props.chapterId, () => {
  handleReadTrack()
  skeleton.value = true
  chapter.value = null
  content.value = ''
  loadChapter()
  resetReadComplete()
})

watch(() => route.fullPath, () => {
  popupTOC.value = false
  popupTheme.value = false
})

watch(() => popupRecharge.value, (val) => {
  if (!val) {
    const newQuery = { ...route.query, isShowpopupRecharge: '0' }
    router.replace({ path: route.path, query: newQuery })
    const leaveTime = Date.now()
    directPageTimeReport('topup', leaveTime - enterTime.value, `content:${props.contentId}:${props.chapterId}`)
  }
  else {
    enterTime.value = Date.now()
    pageViewTrack('topup')
  }
})

watch(() => route.query.recommend, (val) => {
  if (!val)
    showRecommend.value = false
})

watch(() => isTriggerWheelOrSwipeUp.value, (val) => {
  if (val && !popupTOC.value && isLastChapter.value && !popupRecharge.value) {
    setTimeout(() => {
      if (y.value === 0 || (y.value > 0 && arrivedState.bottom))
        finishRecommend()
    }, 100)
  }
})

watchThrottled(y, async () => {
  !popupRecharge.value && reportScrollLocation(y.value)
  handleReadComplete()
}, { throttle: 2000, leading: true })

// Unmounted
onUnmounted(() => {
  handleReadTrack()
  window.removeEventListener('beforeunload', handleCloseEvent)
  removeWheelOrSwipeUpListener()
})
</script>

<template>
  <div :class="`brand-${brand}`">
    <!-- 骨架屏 -->
    <div v-if="skeleton" class="min-h-100dvh" :style="`background: ${theme.background}`">
      <div class="pt-20vh flex justify-center">
        <BasicSpinner class="mx-auto" />
      </div>
    </div>

    <!-- 主容器 -->
    <div v-else>
      <div ref="containerRef" :class="`h-100dvh overflow-scroll color-${theme.background.replace('#', '')}`" :style="`background: ${theme.background}`">
        <!-- 下载 App Banner -->
        <BizDownloadApp
          v-if="showDownloadApp"
          :id="contentId"
          position="fixed"
          class="fixed top-0 w-full"
          :index="chapter?.sequenceNo"
          from="chapter"
        />

        <!-- 导航栏 -->
        <div
          class="fixed w-full z-50"
          :class="{
            'top-60px': bannerShow,
            'top-0': !bannerShow,
          }"
          style="transform: translate(0)"
        >
          <BasicNavbar v-show="y === 0 || !popupTheme" scale :title="chapter?.contentChapterName" />
          <div
            v-if="popupTheme && y !== 0" class="w-full fixed top-0 flex items-center z-10"
            :style="`background: ${theme.background}`"
          >
            <div class="p-2 pl-4" @click="gotoBack()">
              <i-icon-park-outline-left class="w-6 h-6" />
            </div>
          </div>

          <!-- Save URL 按钮 -->
          <div class="absolute right-12px top-12px z-99 flex items-end">
            <div v-if="showSaveUrl" class="theme-save-btn w-80px h-28px flex items-center justify-center text-12px font-medium rounded-14px" @click="showGuidePopup = true">
              Save Url
            </div>
          </div>
        </div>

        <!-- 章节内容 -->
        <div
          v-if="chapter"
          ref="chapterRef"
          class="px-20px pb-10 min-h-100dvh"
          :class="{
            'pt-24': bannerShow,
            'pt-10': !bannerShow,
          }"
          @click="onToggleTheme"
        >
          <!-- 引导语 -->
          <ChapterLeadSentence
            :landing-list="landingList"
            :sequence-no="chapter?.sequenceNo"
            :lead-sentence-style="THEME_FONT.leadSentence"
          />

          <!-- 章节标题 -->
          <p :style="THEME_FONT.chapterTitle" class="chapter-title font-800">
            {{ chapter?.contentChapterName }}
          </p>

          <!-- 章节正文 -->
          <div :style="THEME_FONT.chapterContent" class="chapter-content">
            <p v-for="(item, index) in paragraphs" :key="index" :style="THEME_FONT.paragraphSpacing">
              {{ item }}
            </p>
          </div>

          <!-- 解锁支付模块 -->
          <ChapterUnlock
            v-if="!showRecommend"
            ref="chapterUnlockRef"
            :chapter="chapter"
            :show-download-app="showDownloadApp"
            :content-id="contentId"
            @unlock-chapter="onUnlockChapter"
            @call-app="handleCallApp"
          />

          <!-- 导航按钮 -->
          <ChapterNavigation
            :chapter="chapter"
            :show-recommend="showRecommend"
            @prev-chapter="onPrevChapter"
            @next-chapter="onNextChapter"
            @open-toc="onOpenTOC"
            @unlock-chapter="onUnlockChapter"
          />

          <!-- 推荐模块 -->
          <ChapterRecommend
            v-if="showRecommend"
            class="mt-24px"
            :book-id="contentId"
            @read="appendRecommendParams"
            @collection="appendRecommendParams"
            @error="showRecommend = false"
          />
        </div>

        <!-- 主题设置弹窗 -->
        <ChapterThemeSettings
          v-model="popupTheme"
          :chapter="chapter"
          @prev-chapter="onPrevChapter"
          @next-chapter="onNextChapter"
          @open-toc="onOpenTOC"
        />

        <!-- 目录弹窗 -->
        <BizPopupBookTOC
          v-model="popupTOC"
          :book="book"
          :content-id="props.contentId"
          :chapter-id="props.chapterId"
          :check-pass-goto-chapter="() => onTOCCheckPass()"
        />

        <!-- 充值弹窗 -->
        <ChapterPayTemplate
          v-if="popupRecharge"
          v-model="popupRecharge"
          :content-id="props.contentId"
          :content-chapter-id="props.chapterId"
          :book-name="book?.contentName"
          :chapter-name="chapter?.contentChapterName"
          :serial-status="book?.finishStatus"
          @success="appendRecommendParams"
        />

        <!-- 充值成功弹窗 -->
        <ChapterCoinsSuccess v-model="coinsSuccess" :discount-pay-info="discountPayInfo" @close="handleCoinsSuccess" />

        <!-- 引导弹窗 -->
        <WelfareGuideOpen v-model="showGuidePopup" />

        <!-- 完读加载中 -->
        <div v-if="showCompleteRecommendLoading" class="fixed top-0 left-0 flex justify-center items-center w-full h-full">
          <BasicSpinner />
        </div>
      </div>
    </div>
  </div>
</template>

<style lang="scss">
// 引入主题样式(不使用 scoped,让子组件也能使用这些全局主题类)
@import './styles/chapter-theme.scss';
</style>

<style lang="scss" scoped>
// 其他通用样式(使用 scoped 避免污染全局)
.recharge-list {
  max-height: 70vh;
  overflow-y: auto;
}

.popupRecharge {
  background: #fff;
  background-size: 100% auto;
  background-repeat: no-repeat;
}

.slide-enter-active {
  transition: all 0.2s ease;
  transform: translateY(0%);
}

.slide-leave-active {
  transition: all 0.3s ease;
}

.slide-enter,
.slide-leave-to {
  transform: translateY(-100%);
}
</style>
